import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { ApiError, apiFetch, setAuthToken } from './api'

// Session state for the whole app.
//
// This is the replacement for the legacy `Global.asax` per-request principal
// rebuild — the single line that made all 144 `HasPermission` checks work.
// Here the principal travels in a signed token and is decoded per request on
// the server; the client keeps a copy purely to decide what to render.
//
// Rendering decisions are NOT access control. The legacy application made that
// mistake — 0 of 80 of its permission checks denied anything, they only chose
// whether a control was drawn. Every privileged call here is also refused
// server-side.

export interface User {
  user_id: number
  username: string
  initials: string | null
  reading_physician_id: number | null
  roles: string[]
  permissions: number[]
  permission_names: string[]
  is_transcriptionist: boolean
  is_reading_physician: boolean
  // The named operations this user may perform, from the server's own
  // capability map (`domain/auth/policy.py`). Prefer `may('report.complete')`
  // over `can(2)`: the permission id is the mechanism, the capability is the
  // meaning, and only one of them survives the model changing.
  capabilities: string[]
}

interface AuthState {
  user: User | null
  loading: boolean
  signIn: (username: string, password: string) => Promise<void>
  signOut: () => void
  can: (permissionId: number) => boolean
  may: (capability: string) => boolean
}

const STORAGE_KEY = 'crm.token'
const AuthContext = createContext<AuthState | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // Restore a session on load. A token that no longer verifies is discarded
  // silently — an expired session should look like being signed out, not like
  // an error.
  useEffect(() => {
    const token = localStorage.getItem(STORAGE_KEY)
    if (!token) {
      setLoading(false)
      return
    }
    setAuthToken(token)
    apiFetch<User>('/api/v1/auth/me')
      .then(setUser)
      .catch(() => {
        localStorage.removeItem(STORAGE_KEY)
        setAuthToken(null)
      })
      .finally(() => setLoading(false))
  }, [])

  const signIn = useCallback(async (username: string, password: string) => {
    const res = await apiFetch<{ token: string; user: User }>('/api/v1/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    })
    localStorage.setItem(STORAGE_KEY, res.token)
    setAuthToken(res.token)
    setUser(res.user)
  }, [])

  const signOut = useCallback(() => {
    localStorage.removeItem(STORAGE_KEY)
    setAuthToken(null)
    setUser(null)
  }, [])

  const can = useCallback(
    (permissionId: number) => !!user?.permissions.includes(permissionId),
    [user],
  )

  // Drawing only. The server refuses the same capability independently, so a
  // stale or tampered copy of this list changes what is shown and never what
  // is permitted.
  const may = useCallback(
    (capability: string) => !!user?.capabilities?.includes(capability),
    [user],
  )

  const value = useMemo(
    () => ({ user, loading, signIn, signOut, can, may }),
    [user, loading, signIn, signOut, can, may],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider')
  return ctx
}

/** Gate a route. Sends an unauthenticated user to the sign-in page and
 *  remembers where they were going. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth()
  const location = useLocation()

  if (loading) return <main className="page"><p className="muted">Loading…</p></main>
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />
  return <>{children}</>
}

export { ApiError }
